/**
 * Générateur — Automatisme : positionner sur une échelle de probabilité les
 * évènements du type : impossible ; certain ; pile (pièce équilibrée) ; une
 * valeur d'un dé équilibré ; une couleur de boule dans une urne ; ne pas
 * gagner au loto ; obtenir 10 fois de suite la valeur 1 avec un dé.
 * Attendu ID : 1081 | Niveau : 5ème | Domaine : Probabilités
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Placer sur l'échelle         (QCM de position + classer, carte FUSIONNÉE)
 *   - Plus ou moins probable ?     (QCM comparaison, sans calcul)
 *   - Les extrémités de l'échelle  (V/F logique, cadence raisonnement)
 *
 * ── Plan de cartes validé (2026-09-01) ─────────────────────────────────────
 * Source : Automatisme n°43 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * LIMITE DE PÉRIMÈTRE : « positionner ne suppose pas de savoir calculer » —
 * on compare aux repères 0 ; 0,5 ; 1 (fiche n°43) ; le vocabulaire spécifique
 * (expérience, issue, univers) n'est PAS exigible en autonomie (P7/P8) : les
 * énoncés parlent en mots d'élève, « évènement » reste un mot de consigne.
 * INVARIANTS :
 *   - chaque évènement du vivier porte sa probabilité EXACTE (interne, jamais
 *     montrée) ; sa zone est RECOMPTÉE depuis p par un classifieur unique —
 *     throw si contradiction avec l'étiquette du vivier ;
 *   - obstacle P11 servi : « presque impossible ≠ impossible », les
 *     extrémités sont réservées — chaque QCM de position propose une
 *     extrémité, le V/F matérialise la confusion, la comparaison ose les
 *     paires 0 vs presque-0 et 1 vs presque-1 ;
 *   - zones QUALITATIVES exclusives par construction : « entre 0 et 0,5 »
 *     n'est jamais proposé en même temps que « très près de 0 » (aucune
 *     ambiguïté de bord) ;
 *   - échelle SVG (0 ; 0,25 ; 0,5 ; 0,75 ; 1 + impossible/certain) en appui
 *     de chaque tirage ; repères décimaux à 2 décimales max (P3) ;
 *   - différenciation gen_977 (ère descripteurs) : urnes de boules (mot de
 *     l'intitulé), zones qualitatives (pas de valeurs 1/6 exactes), formats
 *     comparaison et V/F extrémités propres à cet automatisme.
 */
import {
    randomChoice, shuffle,
    makeQcm, makeVraiFaux, makeClasser,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1081;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Fusion revue 2026-09-01 (option A, décision prof) : le QCM de position et
// le classer puisent dans les MÊMES viviers et évaluent le même geste (situer
// un évènement dans une zone) — deux interactions d'une seule tâche, une
// seule carte. Comparaison et V/F extrémités restent des cartes à part.
const L_PLACER = 'Placer sur l\'échelle';
const L_COMPARER = 'Plus ou moins probable ?';
const L_EXTREMITES = 'Les extrémités de l\'échelle';

// ─── L'échelle de probabilité (appui visuel, ne révèle aucune position) ────

const svgEchelle1081 = () => {
    const y = 52; const x0 = 40; const x1 = 380;
    const tick = (f, label) => {
        const x = (x0 + f * (x1 - x0)).toFixed(1);
        return `<line x1="${x}" y1="${y - 8}" x2="${x}" y2="${y + 8}" stroke="#334155" stroke-width="2"/>`
            + `<text x="${x}" y="${y + 26}" font-size="13" fill="#1e293b" text-anchor="middle">${label}</text>`;
    };
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 420 92" width="420" height="92" font-family="Arial">`
        + `<line x1="${x0}" y1="${y}" x2="${x1}" y2="${y}" stroke="#334155" stroke-width="2.5"/>`
        + tick(0, '0') + tick(0.25, '0,25') + tick(0.5, '0,5') + tick(0.75, '0,75') + tick(1, '1')
        + `<text x="${x0}" y="${y - 16}" font-size="12" fill="#475569" text-anchor="start">impossible</text>`
        + `<text x="${x1}" y="${y - 16}" font-size="12" fill="#475569" text-anchor="end">certain</text>`
        + '</svg>';
};
const elEchelle1081 = () => imgElement(svgEchelle1081(), 'compact');

// ─── Le domaine : zones et évènements du répertoire de l'intitulé ──────────

const POSITIONS_1081 = {
    zero: 'À 0 exactement',
    presqueZero: 'Très près de 0, sans être à 0',
    entreBas: 'Entre 0 et 0,5, sans être à 0 ni à 0,5',
    demi: 'À 0,5 exactement',
    entreHaut: 'Entre 0,5 et 1, sans être à 0,5 ni à 1',
    presqueUn: 'Très près de 1, sans être à 1',
    un: 'À 1 exactement',
};

// Probabilités EXACTES internes (jamais montrées) : elles fondent le
// recomptage de zone et l'ordre des comparaisons. Les « presque » portent
// leur vraie valeur approchée ((1/6)^10 ≈ 1,7·10⁻⁸, loto ≈ 5·10⁻⁸…).
const EVENEMENTS_1081 = {
    zero: [
        { t: 'Obtenir 7 en lançant un dé équilibré à six faces', p: 0 },
        { t: 'Tirer une boule verte dans une urne qui ne contient que des boules rouges', p: 0 },
        { t: 'Obtenir à la fois pile et face en un seul lancer de pièce', p: 0 },
        { t: 'Obtenir 0 en lançant un dé équilibré à six faces', p: 0 },
    ],
    presqueZero: [
        { t: 'Obtenir 10 fois de suite la valeur 1 en lançant un dé à six faces', p: 1.7e-8 },
        { t: 'Obtenir la bonne combinaison au loto avec un seul bulletin', p: 5e-8 },
        { t: 'Obtenir 20 fois de suite pile en lançant une pièce équilibrée', p: 1e-6 },
        { t: 'Obtenir 15 fois de suite face en lançant une pièce équilibrée', p: 3.1e-5 },
    ],
    entreBas: [
        { t: 'Obtenir 4 en lançant un dé équilibré à six faces', p: 1 / 6 },
        { t: 'Obtenir 2 en lançant un dé équilibré à six faces', p: 1 / 6 },
        { t: 'Tirer la boule verte dans une urne qui contient 3 boules rouges et 1 boule verte', p: 0.25 },
        { t: 'Obtenir 5 en lançant un dé équilibré à six faces', p: 1 / 6 },
        { t: 'Tirer la boule verte dans une urne qui contient 4 boules rouges et 1 boule verte', p: 0.2 },
    ],
    demi: [
        { t: 'Obtenir pile en lançant une pièce équilibrée', p: 0.5 },
        { t: 'Obtenir face en lançant une pièce équilibrée', p: 0.5 },
        { t: 'Tirer une boule rouge dans une urne qui contient 2 boules rouges et 2 boules vertes', p: 0.5 },
        { t: 'Obtenir un nombre pair en lançant un dé équilibré à six faces', p: 0.5 },
        { t: 'Tirer une boule verte dans une urne qui contient 3 boules rouges et 3 boules vertes', p: 0.5 },
    ],
    entreHaut: [
        { t: 'Tirer une boule rouge dans une urne qui contient 3 boules rouges et 1 boule verte', p: 0.75 },
        { t: 'Ne pas obtenir 6 en lançant un dé équilibré à six faces', p: 5 / 6 },
        { t: 'Tirer une boule rouge dans une urne qui contient 4 boules rouges et 1 boule verte', p: 0.8 },
    ],
    presqueUn: [
        { t: 'Ne pas obtenir la bonne combinaison au loto', p: 1 - 5e-8 },
        { t: 'Obtenir au moins une fois pile en lançant 20 fois une pièce équilibrée', p: 1 - 1e-6 },
        { t: 'Ne pas obtenir 10 fois de suite la valeur 1 en lançant un dé à six faces', p: 1 - 1.7e-8 },
    ],
    un: [
        // Reformulé (revue 2026-09-01, A7) : « entre 1 et 6 » se lit aussi
        // bornes EXCLUES — l'énumération ne laisse aucune lecture double.
        { t: 'Obtenir 1, 2, 3, 4, 5 ou 6 en lançant un dé équilibré à six faces', p: 1 },
        { t: 'Tirer une boule rouge dans une urne qui ne contient que des boules rouges', p: 1 },
        { t: 'Obtenir pile ou face en lançant une pièce', p: 1 },
        { t: 'Obtenir un nombre plus petit que 7 en lançant un dé équilibré à six faces', p: 1 },
    ],
};

/** Classifieur UNIQUE zone ← probabilité : le juge du gen entier. */
const zoneDepuisP = (p) => {
    if (p === 0) return 'zero';
    if (p === 1) return 'un';
    if (p === 0.5) return 'demi';
    if (p < 0.001) return 'presqueZero';
    if (p > 0.999) return 'presqueUn';
    return p < 0.5 ? 'entreBas' : 'entreHaut';
};

/** Tire un évènement d'une zone, vérifié contre le classifieur (throw). */
const tirerEvenement = (zone) => {
    const ev = randomChoice(EVENEMENTS_1081[zone]);
    if (zoneDepuisP(ev.p) !== zone) {
        throw new Error(`gen_1081 : évènement mal étiqueté (« ${ev.t} » n'est pas ${zone})`);
    }
    return ev;
};

// ─── Format 1 : QCM de position (une seule zone défendable) ────────────────
// Les distracteurs sont TAILLÉS par zone : jamais « entre 0 et 0,5 » face à
// « très près de 0 » (bords ambigus), toujours une EXTRÉMITÉ dans le lot
// (obstacle P11 : la tentation du 0 ou du 1).

const DISTRACTEURS_POSITION = {
    zero: ['presqueZero', 'demi', 'un'],
    un: ['presqueUn', 'demi', 'zero'],
    demi: ['zero', 'entreBas', 'un'],
    presqueZero: ['zero', 'demi', 'presqueUn'],
    presqueUn: ['un', 'demi', 'presqueZero'],
    entreBas: ['zero', 'demi', 'entreHaut'],
    entreHaut: ['un', 'demi', 'entreBas'],
};

// Anti-répétition immédiate (recette standard).
let derniereSignaturePosition1081 = null;

const genPositionQcm = () => {
    let payload = construirePositionQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignaturePosition1081; t++) {
        payload = construirePositionQcm();
    }
    derniereSignaturePosition1081 = payload.texte;
    return payload;
};

const construirePositionQcm = () => {
    const zone = randomChoice(Object.keys(EVENEMENTS_1081));
    const ev = tirerEvenement(zone);
    const options = [zone, ...DISTRACTEURS_POSITION[zone]];
    if (!options.includes('zero') && !options.includes('un')) {
        throw new Error('gen_1081 : aucune extrémité proposée (obstacle P11 non servi)');
    }
    const texte = `<p>Sur l'échelle de probabilité, où se place l'évènement suivant ?</p><p>« ${ev.t} »</p>`;
    const payload = makeQcm(
        options.map((z) => POSITIONS_1081[z]),
        POSITIONS_1081[zone],
        texte,
        { ...opts(L_PLACER), elements_additionnels: elEchelle1081() },
    );
    payload._meta.params = { zone, p: ev.p };
    return payload;
};

// ─── Format 2 : classer sur l'échelle (4 positions, 4 évènements) ──────────
// Zones franches uniquement (pas de « entre … ») : les cinq positions que la
// série flash institue, dont on tire 4 — un évènement par position.

const ZONES_CLASSER = ['zero', 'presqueZero', 'demi', 'presqueUn', 'un'];
const ORDRE_ECHELLE = ['zero', 'presqueZero', 'entreBas', 'demi', 'entreHaut', 'presqueUn', 'un'];

// Anti-répétition immédiate (recette standard).
let derniereSignatureClasser1081 = null;

const genEchelleClasser = () => {
    let payload = construireEchelleClasser();
    for (let t = 0; t < 8 && payload.contenu.bonne_reponse === derniereSignatureClasser1081; t++) {
        payload = construireEchelleClasser();
    }
    derniereSignatureClasser1081 = payload.contenu.bonne_reponse;
    return payload;
};

const construireEchelleClasser = () => {
    const zones = shuffle([...ZONES_CLASSER]).slice(0, 4)
        .sort((a, b) => ORDRE_ECHELLE.indexOf(a) - ORDRE_ECHELLE.indexOf(b));
    const categories = zones.map((z) => ({ name: POSITIONS_1081[z], items: [tirerEvenement(z).t] }));
    const payload = makeClasser(
        categories,
        '<p>Range chaque évènement à sa place sur l\'échelle de probabilité.</p>',
        { ...opts(L_PLACER), elements_additionnels: elEchelle1081() },
    );
    payload._meta.params = { zones };
    return payload;
};

// ─── Format 3 : comparer deux évènements (sans calcul) ─────────────────────
// Paires à ordre FRANC : zones distinctes, sauf le cas d'égalité (deux
// évènements de zone « demi », formulations différentes). Paires trop fines
// interdites (presque-0 vs entre-bas, presque-1 vs entre-haut : l'écart ne
// se juge pas sans calcul). Les paires 0 vs presque-0 et 1 vs presque-1 sont
// VOULUES : c'est l'obstacle P11 en acte (le possible bat l'impossible).

const PAIRES_INTERDITES = [['presqueZero', 'entreBas'], ['presqueUn', 'entreHaut']];
const EGALITE = 'Les deux sont aussi probables.';

// Anti-répétition immédiate (recette standard).
let derniereSignatureComparer1081 = null;

const genComparerQcm = () => {
    let payload = construireComparerQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureComparer1081; t++) {
        payload = construireComparerQcm();
    }
    derniereSignatureComparer1081 = payload.texte;
    return payload;
};

const construireComparerQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        let evA; let evB;
        if (Math.random() < 0.25) {
            // Égalité : deux formulations distinctes de la zone « demi ».
            const deux = shuffle([...EVENEMENTS_1081.demi]).slice(0, 2);
            [evA, evB] = deux;
        } else {
            const zones = shuffle(Object.keys(EVENEMENTS_1081)).slice(0, 2);
            if (PAIRES_INTERDITES.some(([a, b]) => zones.includes(a) && zones.includes(b))) continue;
            evA = tirerEvenement(zones[0]);
            evB = tirerEvenement(zones[1]);
        }
        if (evA.t === evB.t) continue;

        const bonne = evA.p === evB.p ? EGALITE : (evA.p > evB.p ? evA.t : evB.t);
        const texte = '<p>Lequel de ces deux évènements est le plus probable ?</p>'
            + `<p>« ${evA.t} »</p><p>« ${evB.t} »</p>`;
        const payload = makeQcm([evA.t, evB.t, EGALITE], bonne, texte, opts(L_COMPARER));
        payload._meta.params = { pA: evA.p, pB: evB.p, egalite: evA.p === evB.p };
        return payload;
    }
    throw new Error('gen_1081 : paire de comparaison introuvable après 40 essais');
};

// ─── Format 4 : V/F logique (les extrémités sont réservées) ────────────────

const POOL_EXTREMITES = [
    { l: 'La graduation 0 est réservée aux évènements qui ne peuvent jamais se produire.', v: true },
    { l: 'Un évènement certain se place à 1 sur l\'échelle de probabilité.', v: true },
    // Reformulé (revue 2026-09-01, A7) : « comprise entre 0 et 1 » se lit
    // aussi bornes exclues (l'impossible, à 0, rendrait la phrase fausse).
    // Ligne indépendante sur l'échelle — pas la négation d'une ligne du pool.
    { l: 'Plus un évènement est probable, plus il se place à droite sur l\'échelle.', v: true },
    { l: 'Obtenir pile avec une pièce équilibrée se place exactement à 0,5.', v: true },
    { l: 'Un évènement très peu probable se place à 0 sur l\'échelle.', v: false },
    { l: 'Ne pas obtenir la bonne combinaison au loto se place exactement à 1.', v: false },
    { l: 'Un évènement peut avoir une probabilité plus grande que 1.', v: false },
    { l: 'Obtenir 10 fois de suite la valeur 1 avec un dé se place exactement à 0.', v: false },
    // Élargissement (revue 2026-09-01) : pool porté à 6 V / 6 F pour interdire
    // STRICTEMENT toute reprise d'une ligne au tirage suivant.
    { l: 'Deux évènements également probables se placent au même endroit sur l\'échelle.', v: true },
    { l: 'Obtenir face avec une pièce équilibrée se place au même endroit qu\'obtenir pile.', v: true },
    { l: 'Un évènement placé à 0,2 est plus probable qu\'un évènement placé à 0,7.', v: false },
    { l: 'L\'échelle de probabilité va de 0 à 10.', v: false },
];

// INTERDICTION STRICTE de reprise (revue 2026-09-01, décision prof) : aucune
// ligne du tirage précédent ne ressort à l'actualisation suivante — pool de
// 12 (6 V / 6 F), la mixité reste toujours atteignable après exclusion.
let derniersServisVf1081 = [];

const genExtremitesVf = () => {
    let tirage = null;
    for (let essai = 0; essai < 60 && !tirage; essai++) {
        const dispo = POOL_EXTREMITES.filter((it) => !derniersServisVf1081.includes(it.l));
        const cand = shuffle(dispo).slice(0, 4);
        const nbFaux = cand.filter((it) => !it.v).length;
        if (nbFaux >= 1 && nbFaux <= 3) tirage = cand;
    }
    if (!tirage) throw new Error('gen_1081 : tirage V/F extrémités sans mixité après 60 essais');
    derniersServisVf1081 = tirage.map((it) => it.l);

    const payload = makeVraiFaux(
        tirage.map((it) => it.l),
        tirage.map((it) => (it.v ? 0 : 1)),
        '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
        { ...opts(L_EXTREMITES), elements_additionnels: elEchelle1081() },
    );
    // Métadonnée DORMANTE (convention gen_1039) : cadence de raisonnement.
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
    return payload;
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    position_qcm_unique: genPositionQcm,
    echelle_classer: genEchelleClasser,
    comparer_qcm_unique: genComparerQcm,
    extremites_tableau_vf: genExtremitesVf,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    {
        // Fusion revue 2026-09-01 : QCM de position + classer, une seule tâche.
        id: 'placer',
        label: L_PLACER,
        formatsList: [...buildFormats('position'), ...buildFormats('echelle')],
    },
    { id: 'comparer', label: L_COMPARER, formatsList: buildFormats('comparer') },
    { id: 'extremites', label: L_EXTREMITES, formatsList: buildFormats('extremites') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
